// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.101Z
// Collection: agentRentals/$rentalId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Immutable onchain payment record for renting an agent for a fixed TIME WINDOW (e.g. 1-4 hours or 1-4 days) instead of paying per-call. The renter prepays once; during [startedAt, expiresAt] they chat without per-call charges. Mirrors callRecords: the onchain create hook performs the split payment atomically in native SOL: creatorAmount to the agent creator and platformAmount to the platform treasury (ADMIN_ADDRESS), both from the renter, in one transaction. isPassthrough: executes onchain but stores offchain for cheap querying.
 */

export interface AgentRentalsRequest {
  agentId: string;
  renterWallet: AddressType;
  creatorWallet: AddressType;
  creatorAmount: number | TimeOperation | IncrementOperation | TokenAmount;
  platformAmount: number | TimeOperation | IncrementOperation | TokenAmount;
  amountPaid: number | TimeOperation | IncrementOperation | TokenAmount;
  tokenMint: string;
  durationSeconds: number | TimeOperation | IncrementOperation | TokenAmount;
  windowLabel: string;
  startedAt: number | TimeOperation | IncrementOperation | TokenAmount;
  expiresAt: number | TimeOperation | IncrementOperation | TokenAmount;
  timestamp: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface AgentRentalsResponse {
  agentId: string;
  renterWallet: string;
  creatorWallet: string;
  creatorAmount: number;
  platformAmount: number;
  amountPaid: number;
  tokenMint: string;
  durationSeconds: number;
  windowLabel: string;
  startedAt: number;
  expiresAt: number;
  timestamp: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a AgentRentals operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildAgentRentals(rentalId: string, data: AgentRentalsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `agentRentals/${rentalId}`, document: doc };
}

/**
 * Only the paying renter (caller must equal @newData.renterWallet) can create their own rental record. creatorAmount and platformAmount must be >= 0 and are in LAMPORTS (native SOL, 9 decimals; 1 SOL = 1,000,000,000 lamports). The onchain create hook transfers creatorAmount lamports of SOL from renter to @newData.creatorWallet AND platformAmount lamports of SOL from renter to ADMIN_ADDRESS in one atomic transaction. amountPaid should equal creatorAmount + platformAmount (lamports). tokenMint should be the SOL constant (@constants.SOL). durationSeconds is the rented window length in seconds (e.g. 3600 = 1h, 86400 = 1 day); windowLabel is a human label like '2h' or '3d'; startedAt is Unix seconds when the rental begins; expiresAt = startedAt + durationSeconds; timestamp is Unix seconds when the record was created. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setAgentRentals(rentalId: string, data: AgentRentalsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`agentRentals/${rentalId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting AgentRentals: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public (required for onchain). Anyone can read rental records for transparency.
   (Get Single Item)
 */
export async function getAgentRentals(rentalId: string): Promise<AgentRentalsResponse | null> {
  try {
    const result = await get(`agentRentals/${rentalId}`);
    return result as AgentRentalsResponse | null;
  } catch (error) {
    console.error(`Error getting AgentRentals: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single AgentRentals document. (
  Read Operation Details: Public (required for onchain). Anyone can read rental records for transparency.
  )
 */
export function subscribeAgentRentals(callback: (data: AgentRentalsResponse | null) => void, rentalId: string): Promise<() => Promise<void>> {
  return subscribe(`agentRentals/${rentalId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as AgentRentalsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to AgentRentals at path '${`agentRentals/${rentalId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many AgentRentals items from collection agentRentals
 
  Read Operation Details: Public (required for onchain). Anyone can read rental records for transparency.
  
 */
export async function getManyAgentRentals(filter: string = ""): Promise<AgentRentalsResponse[]> {
  try {
    const results = await get("agentRentals", { prompt: filter });
    return Array.isArray(results) ? results as AgentRentalsResponse[] : [];
  } catch (error) {
    console.error(`Error getting AgentRentals collection at path 'agentRentals': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in AgentRentals collection at agentRentals
 
  Read Operation Details: Public (required for onchain). Anyone can read rental records for transparency.
  
 */
export function subscribeManyAgentRentals(callback: (data: AgentRentalsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("agentRentals", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AgentRentalsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to AgentRentals collection at path '${"agentRentals"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all AgentRentals items from collection agentRentals
 
  Read Operation Details: Public (required for onchain). Anyone can read rental records for transparency.
  
 */
export async function getAllAgentRentals(filter: string = ""): Promise<AgentRentalsResponse[]> {
  try {
    const results = await get("agentRentals", { prompt: filter });
    return Array.isArray(results) ? results as AgentRentalsResponse[] : [];
  } catch (error) {
    console.error(`Error getting AgentRentals collection at path 'agentRentals': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in AgentRentals collection at agentRentals
 
  Read Operation Details: Public (required for onchain). Anyone can read rental records for transparency.
  
 */
export function subscribeAllAgentRentals(callback: (data: AgentRentalsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("agentRentals", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AgentRentalsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to AgentRentals collection at path '${"agentRentals"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count AgentRentals items in collection agentRentals.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countAgentRentals(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("agentRentals", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting AgentRentals at path 'agentRentals': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on AgentRentals items in collection agentRentals.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateAgentRentals(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("agentRentals", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating AgentRentals at path 'agentRentals': ${formatError(error)}`);
    return { value: 0 };
  }
}

